"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/providers/AuthProvider";
// Removed client-side Supabase - using API routes instead
import { logger } from "@/utils/logger";

interface UnreadCount {
  total: number;
  byConversation: Record<string, number>;
}

interface UseMessageUnreadCountReturn {
  unreadCount: UnreadCount;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Lightweight hook for fetching message unread count
 * Uses polling instead of WebSocket to keep the app lightweight
 * Polls every 45 seconds (between 30-60s as specified)
 */
export function useMessageUnreadCount(): UseMessageUnreadCountReturn {
  const { user } = useAuth();

  const [unreadCount, setUnreadCount] = useState<UnreadCount>({
    total: 0,
    byConversation: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount({ total: 0, byConversation: {} });
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // Fetch unread count via API route
      const response = await fetch("/api/messages/unread-count", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.warn(
          "Error fetching unread count",
          {
            component: "useMessageUnreadCount",
            action: "fetchUnreadCount",
            userId: user.id,
            error: errorData.error || "Failed to fetch unread count",
          },
          new Error(errorData.error || "Failed to fetch unread count")
        );
        setUnreadCount({ total: 0, byConversation: {} });
        return;
      }

      const data = await response.json();
      const unreadCountData = data.data || { total: 0, byConversation: {} };
      setUnreadCount(unreadCountData);
    } catch (err) {
      logger.error(
        "Error fetching unread count",
        {
          component: "useMessageUnreadCount",
          action: "fetchUnreadCount",
          userId: user?.id,
        },
        err instanceof Error ? err : new Error("Unknown error")
      );
      setUnreadCount({ total: 0, byConversation: {} });
      setError("Unable to load message counts. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Initial load on mount only - no polling to avoid API spam
  // Polling was removed because this hook was being used in multiple components
  // in the navbar, each creating their own polling interval
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    } else {
      setUnreadCount({ total: 0, byConversation: {} });
      setLoading(false);
    }
  }, [user?.id, fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    error,
    refresh: fetchUnreadCount,
  };
}
